import { useEffect, useRef, type ReactNode } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import type { AppDispatch } from '@/store';
import userSelector from '@/store/selectors/userSelector';
import { useGetMunicipalities } from '@/helpers/getMunicipality';
import { setActiveMunicipalityId } from '@/helpers/activeMunicipality';
import { setActiveMunicipality } from '@/store/reducers/municipalitySlice';
import { updateUser } from '@/store/reducers/userSlice';

// Resolve email links against actual memberships before mounting page queries.
const ObligationLinkGate = ({ children }: { children: ReactNode }) => {
  const user = useSelector(userSelector);
  const isClient = user?.type === 'client' && !!user.verifiedEmail && !!user.verifiedByAdmin;
  const [params, setParams] = useSearchParams();
  const requestedId = params.get('obligationMunicipalityId');
  const { data: municipalities, error, refetch } = useGetMunicipalities({ enabled: isClient && !!requestedId });
  const dispatch = useDispatch<AppDispatch>();
  const queryClient = useQueryClient();
  const processingId = useRef<string | null>(null);

  useEffect(() => {
    if (!isClient || !requestedId || !municipalities || processingId.current === requestedId) return;
    processingId.current = requestedId;
    const open = async () => {
      try {
        if (!municipalities.some(municipality => municipality.id === requestedId)) {
          toast.error('K obci z tohoto upozornění nemáte přístup.');
        } else {
          setActiveMunicipalityId(requestedId);
          dispatch(setActiveMunicipality(requestedId));
          await queryClient.invalidateQueries();
          await dispatch(updateUser()).unwrap();
        }
      } catch {
        toast.error('Obec pro toto upozornění se nepodařilo načíst.');
      } finally {
        setParams(previous => {
          const next = new URLSearchParams(previous);
          if (next.get('obligationMunicipalityId') === requestedId) next.delete('obligationMunicipalityId');
          return next;
        }, { replace: true });
        processingId.current = null;
      }
    };
    void open();
  }, [dispatch, isClient, municipalities, queryClient, requestedId, setParams]);

  if (isClient && requestedId && error) return <Alert severity="error" action={<Button color="inherit" onClick={() => void refetch()}>Zkusit znovu</Button>}>Obec z upozornění se nepodařilo načíst.</Alert>;
  if (isClient && requestedId) return <Box sx={{ p: 3 }}><CircularProgress size={24} aria-label="Načítání obce z upozornění" /></Box>;
  return children;
};

export default ObligationLinkGate;
